import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { DateRange } from 'react-day-picker';
import { Filter } from 'lucide-react';

// File type options for document filters
export const FILE_TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'pdf', label: 'PDF' },
  { value: 'docx', label: 'DOCX' },
  { value: 'doc', label: 'DOC' },
  { value: 'txt', label: 'TXT' },
  { value: 'md', label: 'Markdown' },
  { value: 'rtf', label: 'RTF' },
  { value: 'xlsx', label: 'XLSX' },
  { value: 'xls', label: 'XLS' },
  { value: 'csv', label: 'CSV' },
  { value: 'pptx', label: 'PPTX' },
  { value: 'ppt', label: 'PPT' },
  { value: 'png', label: 'PNG' },
  { value: 'jpg', label: 'JPG' },
  { value: 'jpeg', label: 'JPEG' },
  { value: 'gif', label: 'GIF' },
  { value: 'tiff', label: 'TIFF' },
  { value: 'bmp', label: 'BMP' },
];

interface FileTypeFilterProps {
  value: string;
  onChange: (value: string) => void;
  options?: { value: string; label: string }[];
  portalClassName?: string;
}

/**
 * File type select filter
 */
export const FileTypeFilter: React.FC<FileTypeFilterProps> = ({
  value,
  onChange,
  options = FILE_TYPE_OPTIONS,
  portalClassName,
}) => {
  return (
    <div className="space-y-1">
      <label className="text-xs text-muted-foreground">File Type</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder="All Types" />
        </SelectTrigger>
        <SelectContent className={portalClassName}>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

interface DocumentTypeFilterProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  loading?: boolean;
  portalClassName?: string;
}

/**
 * Document type select filter (fetches types from API)
 */
export const DocumentTypeFilter: React.FC<DocumentTypeFilterProps> = ({
  value,
  onChange,
  options,
  loading = false,
  portalClassName,
}) => {
  const allOptions = [{ value: 'all', label: 'All Types' }, ...options];
  return (
    <div className="space-y-1">
      <label className="text-xs text-muted-foreground">Document Type</label>
      <Select value={value} onValueChange={onChange} disabled={loading}>
        <SelectTrigger>
          <SelectValue placeholder={loading ? 'Loading...' : 'All Types'} />
        </SelectTrigger>
        <SelectContent className={portalClassName}>
          {allOptions.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

interface SourceFilterProps {
  value: string;
  onChange: (value: string) => void;
  portalClassName?: string;
}

/**
 * Source filter - matches the per-row "Source" badge in the table:
 *   manual = uploaded from device (Orgain Document.cloud_source IS NULL)
 *   google = Google Drive sync
 *   sharepoint = SharePoint sync
 * Backend translates the value to a SQL filter on `cs.provider`.
 */
export const SourceFilter: React.FC<SourceFilterProps> = ({ value, onChange, portalClassName }) => {
  return (
    <div className="space-y-1">
      <label className="text-xs text-muted-foreground">Source</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder="All" />
        </SelectTrigger>
        <SelectContent className={portalClassName}>
          <SelectItem value="all">All</SelectItem>
          <SelectItem value="manual">Manual upload</SelectItem>
          <SelectItem value="google">Google Drive</SelectItem>
          <SelectItem value="sharepoint">SharePoint</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};

interface KBCategorizedFilterProps {
  value: string;
  onChange: (value: string) => void;
  portalClassName?: string;
}

/**
 * Knowledge Base categorization filter (all / in KB / not in KB)
 */
export const KBCategorizedFilter: React.FC<KBCategorizedFilterProps> = ({
  value,
  onChange,
  portalClassName,
}) => {
  return (
    <div className="space-y-1">
      <label className="text-xs text-muted-foreground">Knowledge Base</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder="All" />
        </SelectTrigger>
        <SelectContent className={portalClassName}>
          <SelectItem value="all">All</SelectItem>
          <SelectItem value="true">In Knowledge Base</SelectItem>
          <SelectItem value="false">Hidden from Users</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};

interface FileSizeFilterProps {
  minValue: string;
  maxValue: string;
  onMinChange: (value: string) => void;
  onMaxChange: (value: string) => void;
}

/**
 * File size range filters (min/max in MB)
 */
export const FileSizeFilter: React.FC<FileSizeFilterProps> = ({
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
}) => {
  return (
    <>
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">Min Size (MB)</label>
        <Input
          type="number"
          value={minValue}
          onChange={(e) => onMinChange(e.target.value)}
          placeholder="Min"
          min="0"
          step="0.1"
        />
      </div>
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">Max Size (MB)</label>
        <Input
          type="number"
          value={maxValue}
          onChange={(e) => onMaxChange(e.target.value)}
          placeholder="Max"
          min="0"
          step="0.1"
        />
      </div>
    </>
  );
};

interface DateRangeFilterProps {
  value: DateRange | undefined;
  onChange: (value: DateRange | undefined) => void;
  label?: string;
  placeholder?: string;
  portalClassName?: string;
}

/**
 * Date range filter
 */
export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  value,
  onChange,
  label = 'Date Range',
  placeholder = 'Select date range',
  portalClassName,
}) => {
  return (
    <div className="space-y-1">
      <label className="text-xs text-muted-foreground">{label}</label>
      <DateRangePicker
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        portalClassName={portalClassName}
      />
    </div>
  );
};

interface KBFilterPanelProps {
  children: React.ReactNode;
  onReset?: () => void;
  showResetButton?: boolean;
  title?: string;
  showTitle?: boolean;
}

/**
 * Shared filter panel container
 * Used in: KBDocumentsPage, KBValidationPage
 */
const KBFilterPanel: React.FC<KBFilterPanelProps> = ({
  children,
  onReset,
  showResetButton = true,
  title = 'Filters',
  showTitle = false,
}) => {
  if (showTitle) {
    return (
      <Card>
        <CardHeader className="py-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {children}
          </div>
          {showResetButton && onReset && (
            <div className="flex justify-end mt-4">
              <Button variant="outline" size="sm" onClick={onReset}>
                Reset Filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-muted/30">
      <CardContent className="py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {children}
        </div>
      </CardContent>
    </Card>
  );
};

export default KBFilterPanel;
